import type { RecurrenceFreq, ScheduleEvent } from "@couples/shared";

import { supabase } from "../supabase";

interface EventRow {
  id: string;
  couple_id: string;
  creator_id: string;
  assigned_to: string | null;
  category_id: string | null;
  title: string;
  memo: string | null;
  all_day: boolean;
  starts_at: string;
  ends_at: string | null;
  recurrence_freq: RecurrenceFreq | null;
  recurrence_interval: number;
  recurrence_until: string | null;
}

const COLUMNS =
  "id, couple_id, creator_id, assigned_to, category_id, title, memo, all_day, starts_at, ends_at, recurrence_freq, recurrence_interval, recurrence_until";

function toEvent(row: EventRow): ScheduleEvent {
  return {
    id: row.id,
    coupleId: row.couple_id,
    creatorId: row.creator_id,
    assignedTo: row.assigned_to,
    categoryId: row.category_id,
    title: row.title,
    memo: row.memo,
    allDay: row.all_day,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    recurrenceFreq: row.recurrence_freq,
    recurrenceInterval: row.recurrence_interval,
    recurrenceUntil: row.recurrence_until,
  };
}

/**
 * 予定を全件取得する。
 *
 * 繰り返し予定は「規則」だけをDBに持ち、展開はクライアント側で行う(→ data-model.md 5章)。
 * 開始日で範囲を絞ると、範囲より前に始まった繰り返し予定を取りこぼすため、
 * 期間では絞り込まない。二人ぶんの予定は件数が少なく、全件取得で十分実用的。
 */
export async function fetchEvents(): Promise<ScheduleEvent[]> {
  const { data, error } = await supabase.from("events").select(COLUMNS).order("starts_at");
  if (error) throw error;
  const rows: EventRow[] = data;
  return rows.map(toEvent);
}

export interface EventInput {
  title: string;
  memo: string | null;
  allDay: boolean;
  assignedTo: string | null;
  categoryId: string | null;
  /** ISO instant */
  startsAt: string;
  endsAt: string | null;
  recurrenceFreq: RecurrenceFreq | null;
  recurrenceInterval: number;
  recurrenceUntil: string | null;
}

function toInsertRow(input: EventInput) {
  return {
    title: input.title,
    memo: input.memo,
    all_day: input.allDay,
    assigned_to: input.assignedTo,
    category_id: input.categoryId,
    starts_at: input.startsAt,
    ends_at: input.endsAt,
    recurrence_freq: input.recurrenceFreq,
    recurrence_interval: input.recurrenceInterval,
    recurrence_until: input.recurrenceUntil,
  };
}

/** couple_id / creator_id は DB 側の既定値でセッションから埋まる(→ 20260813000011_schedule_defaults.sql) */
export async function createEvent(input: EventInput): Promise<ScheduleEvent> {
  const { data, error } = await supabase
    .from("events")
    .insert(toInsertRow(input))
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return toEvent(data);
}

export async function updateEvent(id: string, input: EventInput): Promise<ScheduleEvent> {
  const { data, error } = await supabase
    .from("events")
    .update(toInsertRow(input))
    .eq("id", id)
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return toEvent(data);
}

/**
 * 予定を削除する。
 *
 * 繰り返しの予定を削除するときは、事前に「この回だけ差し替え」(→ lib/schedule/eventExceptions.ts)
 * で作られた単発の予定も一緒に消す。放っておくと、元のシリーズとの繋がりが分からない
 * 単発の予定だけが残ってしまう。
 */
export async function deleteEvent(id: string): Promise<void> {
  const { data: overrides, error: fetchError } = await supabase
    .from("event_exceptions")
    .select("override_event_id")
    .eq("event_id", id)
    .not("override_event_id", "is", null);
  if (fetchError) throw fetchError;
  const overrideIds = (overrides as { override_event_id: string }[]).map(
    (row) => row.override_event_id,
  );
  if (overrideIds.length > 0) {
    const { error } = await supabase.from("events").delete().in("id", overrideIds);
    if (error) throw error;
  }

  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) throw error;
}
